<template>
  <div
    v-if="locations.length > 0"
    class="available-locations pdp__locations"
  >
    <div
      class="available-locations__heading"
    >
      {{ $t('U kunt dit artikel reserveren in de volgende winkels') }}
    </div>

    <div class="available-locations__container">
      <ul
        ref="availablelocations"
        class="available-locations__list"
      >
        <li
          v-for="(location, index) in locations"
          :key="index"
          class="available-locations__item"
        >
          <button
            class="available-locations__button button button--ghost"
            @click.prevent="openReserveAtStore(location.id, location.city)"
          >
            <span>{{ location.city }}</span>
          </button>
        </li>
      </ul>
    </div>

    <client-only>
      <LazyUiDialog
        class="modal-reserve-at-store"
        name="modal-reserve-at-store"
        dialog-type="responsive"
      >
        <template #dialogHeader>
          <span>{{ $t('Reservation {store}', { store: selectedStoreName }) }}</span>
        </template>

        <div class="size-guide__content">
          <slot name="content">
            <FormReserveAtStore
              :store-id="selectedStoreId"
              :sku="currentSku"
              @reservation-complete="handleReservationComplete"
            />
          </slot>
        </div>
      </LazyUiDialog>
    </client-only>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  currentItem?: string[] | string | null
}>()

const meta = inject('meta', ref())
const { loadAvelonStockData, AvelonStockData, pendingAvelonStock } = usePDP(meta)
const modalReserveAtStore = useUiDialog('modal-reserve-at-store')
const selectedStoreId = ref<number | null>(null)
const selectedStoreName = ref<string | null>(null)

onMounted(() => {
  if (pendingAvelonStock) {
    loadAvelonStockData()
  }
})

const openReserveAtStore = (id: string, name: string): void => {
  selectedStoreId.value = parseInt(id)
  selectedStoreName.value = String(name)
  modalReserveAtStore.open()
}

const closeReserveAtStore = (): void => {
  selectedStoreId.value = null
  selectedStoreName.value = null
  modalReserveAtStore.close()
}

const handleReservationComplete = (success: boolean): void => {
  if (success) {
    closeReserveAtStore()
  }
}

const currentSku = computed(() => {
  if (!props.currentItem) return null

  if (Array.isArray(props.currentItem) && props.currentItem.length > 0) {
    return props.currentItem[0].toLowerCase()
  }
  else if (typeof props.currentItem === 'string') {
    return props.currentItem.toLowerCase()
  }

  return null
})

const locations = computed(() => {
  if (pendingAvelonStock.value || !AvelonStockData.value || !currentSku.value) {
    return []
  }

  const getStock = AvelonStockData.value?.getStock
  if (!getStock?.shopStock || !Array.isArray(getStock.shopStock)) {
    return []
  }

  return getStock.shopStock
    .filter((shopData) => {
      if (!shopData?.stockStatus || !Array.isArray(shopData.stockStatus) || !shopData.shop?.city) {
        return false
      }

      return shopData.stockStatus.some(
        status => status?.sku?.toLowerCase?.() === currentSku.value && status.status > 0,
      )
    })
    .map(shopData => shopData.shop)
})
</script>

<style src="~layers/app/components/Product/Availability/AvailableLocations.css" />
